import styles from "./KeywordCloud.module.scss";
import React, { useCallback, useEffect, useRef, useState } from "react";
import * as d3 from "d3";
import cloud from "d3-cloud";
import { Empty, message, Modal } from "antd";
import GranularityZoomButtons from "./GranularityZoomButtons";
import { TIME_FILTER_TYPE } from "Explore/Config/constraint";
import TrackingBreadCrumb from "./TrackingBreadCrumb";
import TimeFilter from "./TimeFilter";
import TimePicker from "./TimePicker";
import useDeviceCheck from "Hooks/useDeviceCheck";
import { Button } from "@mui/material";
import BackspaceIcon from "@mui/icons-material/Backspace";

d3.selection.prototype.graphTransition = function () {
  return this.transition().duration(150).ease(d3.easeCubicOut);
};

const KeywordCloudD3 = ({
  keywords: _keywords = [],
  selectedKeywords = [],
  setSelectedKeywords,
  granularityLevel,
  setGranularityLevel,
  granularityLevelQueue: granularityLevelQueueProps,
  setGranularityLevelQueue: setGranularityLevelQueueProps,
  selectedKeywordsQueue = [],
  setSelectedKeywordsQueue,
  sliderValue,
  setSliderValue,
  timeFilter,
  setTimeFilter,
  timeFilterType = TIME_FILTER_TYPE.SLIDER,
  containerPadding = 60,
  maxFontSize = 26,
  minFontSize = 14,
  padding = 4,
  hideOperation = false,
  scaleFactor = 1.3,
  chart_height = 440,
}) => {
  const { isMobile, isTablet } = useDeviceCheck();
  const svgRef = useRef(null);
  const containerRef = useRef(null);
  const [definitionKeyword, setDefinitionKeyword] = useState(null);
  const [data, setData] = useState([]);
  const [cannotZoomMessageApi, cannotZoomContextHolder] = message.useMessage();
  const [isLoading, setIsLoading] = useState(false);
  const [_granularityLevelQueue, _setGranularityLevelQueue] = useState([]);
  const granularityLevelQueue =
    granularityLevelQueueProps || _granularityLevelQueue;
  const setGranularityLevelQueue =
    setGranularityLevelQueueProps || _setGranularityLevelQueue;

  const normalizeScoreToFontSize = useCallback(
    ({ minScore, maxScore, minFontSize, maxFontSize, scoreToNorm }) => {
      const scoreRange = maxScore - minScore;
      const fontSizeRange = maxFontSize - minFontSize;

      if (scoreRange === 0) return 20;

      const normalizedValue = Math.round(
        ((scoreToNorm - minScore) * fontSizeRange) / scoreRange + minFontSize,
      );

      return Math.min(maxFontSize, Math.max(minFontSize, normalizedValue));
    },
    [],
  );

  const cannotZoomInfoShow = () => {
    cannotZoomMessageApi.open({
      type: "error",
      content: "Please select keywords before zooming in",
      style: {
        marginTop: "7vh",
      },
    });
  };

  // Generate colors based on the base color with slight variations
  const generateDistinctColor = useCallback((d, i) => {
    const hsl1 = d3.hsl(d3.rgb("#0f3460"));
    const hsl2 = d3.hsl(d3.rgb("#60130f"));
    const hsl = i % 2 === 0 ? hsl1 : hsl2;

    // Map percent to a reasonable lightness range (0.1-0.5)
    const lightness = 0.5 - (d.percent || 0) * 0.1;

    const hueVariation = 30; // Increased from -10 to 15 degrees
    const hueOffset = ((i % 7) * hueVariation) / 6 - hueVariation / 2;

    const saturationOffset = ((i % 3) - 1) * 0.15;

    return d3
      .hsl(
        (hsl.h + hueOffset) % 360,
        Math.min(1, Math.max(0.5, hsl.s + saturationOffset)),
        Math.min(0.9, Math.max(0.25, lightness)),
      )
      .toString();
  }, []);

  useEffect(() => {
    setData([]);
    setIsLoading(true);
  }, [granularityLevel, sliderValue, timeFilter]);

  useEffect(() => {
    const [minScore, maxScore] = _keywords.reduce(
      ([min, max], { score = 10 }) => [
        Math.min(min, score),
        Math.max(max, score),
      ],
      [Infinity, -Infinity],
    );

    const res = _keywords.map((item, index) => ({
      ...item,
      real_id: item.id,
      index,
      text: item.value,
      size: normalizeScoreToFontSize({
        minScore,
        maxScore,
        minFontSize,
        maxFontSize,
        scoreToNorm: item.score,
      }),
    }));
    setData(res);
    setIsLoading(false);
  }, [JSON.stringify(_keywords)]);

  // useEffect(() => {
  //   if (selectedKeywords) {
  //     if (
  //       granularityLevel >= 2 &&
  //       selectedKeywordsQueue[granularityLevel - 2]
  //     ) {
  //       setData((prevData) =>
  //         prevData.filter((line) => {
  //           return selectedKeywordsQueue[granularityLevel - 2]
  //             .map((item) => item.value)
  //             .includes(line.text);
  //         })
  //       );
  //     } else if (granularityLevel == 1) {
  //     } else {
  //       setData([]);
  //     }
  //   } else {
  //     setData([]);
  //   }
  // }, [granularityLevel]);
  useEffect(() => {
    if (!svgRef.current) return;

    d3.select(svgRef.current).selectAll("*").remove();

    const updateDimensions = () => {
      const container = containerRef.current;
      if (!container) return;

      const width = container.clientWidth;
      const height = container.clientHeight;

      // Update SVG dimensions
      d3.select(svgRef.current).attr("width", width).attr("height", height);
    };

    // Create resize observer
    const resizeObserver = new ResizeObserver(updateDimensions);
    resizeObserver.observe(containerRef.current);

    // Initialize the graph
    const initialWidth = containerRef.current.clientWidth;
    const initialHeight = containerRef.current.clientHeight;
    const centerX = initialWidth / 2;
    const centerY = initialHeight / 2;

    const svg = d3
      .select(svgRef.current)
      .attr("viewBox", [0, 0, initialWidth, initialHeight]);

    const layout = cloud()
      .size([initialWidth - containerPadding, initialHeight - containerPadding])
      .words(data)
      .padding(padding)
      .rotate(() => ~~0)
      .font("Poppins")
      .fontSize((d) => d.size)
      .on("end", draw);

    function draw(words) {
      // Helper inside draw so it has access to the scope
      const calculateNewPosition = (hoveredWord, currentWord, pushDistance) => {
        const dx = currentWord.x - hoveredWord.x;
        const dy = currentWord.y - hoveredWord.y;
        const dist = Math.sqrt(dx * dx + dy * dy);
        if (dist === 0) return { x: currentWord.x, y: currentWord.y };
        const scale = (dist + pushDistance) / dist;
        return { x: hoveredWord.x + dx * scale, y: hoveredWord.y + dy * scale };
      };

      const group = svg
        .append("g")
        .attr("transform", `translate(${centerX},${centerY})`);

      // Create container groups
      const wordGroups = group
        .selectAll(".word-container")
        .data(words)
        .enter()
        .append("g")
        .attr("class", "word-container")
        .attr("transform", (d) => `translate(${d.x},${d.y})`);

      const texts = wordGroups
        .append("text")
        .attr("class", "word")
        .style("font-size", (d) => `${d.size}px`)
        .style("fill", (d, i) => generateDistinctColor(d, i))
        .attr("text-anchor", "middle")
        .text((d) => d.text)
        .style("cursor", "pointer")
        .style("text-shadow", "0 0 1px rgba(255,255,255,0.5)");

      texts
        .on("click", function (event, d) {
          // Clear existing backgrounds and reset font weights
          group.selectAll(".word-background").remove();
          texts.classed("selected", false).style("font-weight", "normal");

          const textElement = d3.select(this);
          textElement.classed("selected", true).style("font-weight", "bold");

          const bbox = this.getBBox();
          const pad = 2;

          // Insert background behind text within the same group
          d3.select(this.parentNode)
            .insert("rect", "text")
            .attr("class", "word-background")
            .attr("id", `word-background-${d.real_id}`)
            .attr("x", bbox.x - pad)
            .attr("y", bbox.y - pad)
            .attr("width", bbox.width + pad * 2)
            .attr("height", bbox.height + pad * 2)
            .attr("rx", 5)
            .attr("fill", "rgba(255, 255, 255, 0.8)")
            .attr("stroke", textElement.style("fill"))
            .attr("stroke-width", 2);

          setSelectedKeywords([
            {
              real_id: d.id,
              value: d.text,
              id: d.text,
              text: d.text,
              level: d.level,
            },
          ]);
        })
        .on("mouseover", function (event, d) {
          const parent = d3.select(this.parentNode);
          parent.raise();

          // Scale the focused group
          parent
            .graphTransition()
            .attr(
              "transform",
              `translate(${d.x},${d.y}) scale(${scaleFactor})`,
            );

          // Push others
          wordGroups
            .filter((w) => w !== d)
            .each(function (w) {
              const newPos = calculateNewPosition(d, w, d.size * 1.3);
              d3.select(this)
                .graphTransition()
                .attr("transform", `translate(${newPos.x},${newPos.y})`)
                .style("opacity", 0.6);
            });
        })
        .on("mouseout", function (event, d) {
          // Restore all groups to original positions and scale
          wordGroups
            .graphTransition()
            .attr("transform", (w) => `translate(${w.x},${w.y}) scale(1)`)
            .style("opacity", 1);
        });
    }

    layout.start();

    return () => {
      svg.selectAll("*").remove();
    };
  }, [data]);

  return (
    <>
      <div className={styles["keyword-cloud-container"]}>
        {cannotZoomContextHolder}
        {!hideOperation && (
          <div className={styles["operations-tab"]}>
            <GranularityZoomButtons
              keywords={data}
              granularityLevel={granularityLevel}
              setGranularityLevel={setGranularityLevel}
              selectedKeywords={selectedKeywords}
              setSelectedKeywords={setSelectedKeywords}
              selectedKeywordsQueue={selectedKeywordsQueue}
              setSelectedKeywordsQueue={setSelectedKeywordsQueue}
              isLoading={isLoading}
              cannotZoomInfoShow={cannotZoomInfoShow}
              granularityLevelQueue={granularityLevelQueue}
              setGranularityLevelQueue={setGranularityLevelQueue}
            />

            <div className={styles["selected-keywords-display"]}>
              {selectedKeywordsQueue.length > 0 ? (
                <TrackingBreadCrumb
                  selectedKeywords={selectedKeywords}
                  selectedKeywordsQueue={selectedKeywordsQueue}
                />
              ) : (
                <i style={{ color: "var(--grey-text)", fontSize: "13px" }}>
                  You can click a keyword to filter the displayed items
                </i>
              )}
            </div>

            <div className={styles["right-section"]}>
              {selectedKeywords?.length > 0 && (
                <Button
                  color="error"
                  variant="contained"
                  startIcon={<BackspaceIcon />}
                  size="small"
                  onClick={() => {
                    setSelectedKeywords([]);
                    setSelectedKeywordsQueue([]);
                    setGranularityLevel(1);
                    setGranularityLevelQueue([]);
                  }}
                >
                  Clear{isTablet ? "" : " keywords"}
                </Button>
              )}
              {timeFilterType === TIME_FILTER_TYPE.DROPDOWN && (
                <TimeFilter
                  timeFilter={timeFilter}
                  setTimeFilter={setTimeFilter}
                />
              )}
              {timeFilterType === TIME_FILTER_TYPE.SLIDER && (
                <TimePicker
                  value={sliderValue}
                  setValue={setSliderValue}
                  selectedKeywords={selectedKeywords}
                  setSelectedKeywords={setSelectedKeywords}
                  disabled={isLoading}
                />
              )}
            </div>
          </div>
        )}
        <div
          ref={containerRef}
          style={{
            height: chart_height,
            width: "100%",
          }}
        >
          {data.length > 0 ? (
            <svg ref={svgRef} />
          ) : (
            <Empty
              description="No keyword found!"
              style={{
                height: "100%",
                width: "100%",
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
                alignItems: "center",
              }}
            />
          )}
        </div>
      </div>

      <Modal
        style={{ body: { maxHeight: "250px", overflowY: "scroll" } }}
        onClose={() => setDefinitionKeyword(null)}
        onCancel={() => setDefinitionKeyword(null)}
        title={definitionKeyword?.text}
        open={!!definitionKeyword}
        footer={null}
      >
        <p>{definitionKeyword?.definition}</p>
      </Modal>
    </>
  );
};

export default KeywordCloudD3;
